import { Platform } from "@-/com.npmjs.react-native";
import { getGraphicsTier, type GraphicsTier } from "@-/dev.improve.haniverse.lib.graphics-tier";
import {
  welcomeGalaxyMixColor,
  welcomeGalaxyPalette,
  type WelcomeGalaxyAppearance,
  type WelcomeGalaxyPalette,
} from "#welcome-galaxy-palette";
import {
  clearWelcomeGalaxySprites,
  createWelcomeGalaxyGaussian,
  createWelcomeGalaxyRandom,
  welcomeGalaxyColorByte,
  type WelcomeGalaxyAtlasMetrics,
} from "#welcome-galaxy-particles";
import {
  lerp,
  smootherstep,
  welcomeGalaxyPlacement,
  type WelcomeGalaxyViewport,
} from "#welcome-galaxy-timeline";

/**
 * Shared launch sky and the perspective particle exit used for sign-out snapshots. Cold launch
 * uses only the sky: WelcomeHaniWorld grows the splash Hani into its seated pose on an expanding
 * globe, while AuthWelcomeGalaxy reveals a settled galaxy through opacity.
 *
 * On sign-out the full-size snapshot blurs and dissolves while characters and speed lines stream
 * toward the galaxy core. The clock starts at -WELCOME_GALAXY_LAUNCH_DURATION; pure curves share
 * the page clock and its fast-forward. Perspective particles are positioned relative to how far
 * the accelerating camera has travelled since passing them.
 */

/** What the welcome galaxy launches out of: the native splash (a cold launch), or a picture of the app (sign-out). */
export type WelcomeGalaxyLaunchKind = "snapshot" | "splash";

/** How long the launch lasts before the galaxy flight (s): the clock starts at minus this. */
export const WELCOME_GALAXY_LAUNCH_DURATION = 2;
/** Past this moment the launch layer has fully handed over to the galaxy flight. */
export const WELCOME_GALAXY_LAUNCH_END_T = 1;
/** Acceleration of the camera's log-distance (1/s²): it launches from rest. */
const LAUNCH_ACCELERATION = 2.8;
/** How far back (s) each speed line's tail reaches along its path. */
const SPEED_LINE_LAG = 0.07;
/** The bootsplash glyph's ink (`#0a0a0a`, see `writeSplashIconPng`), as a 0–255 channel. */
const INK = 10;
/** Characters fade in as they shrink below this size (dp) after passing the camera… */
const NEAR_FADE_START = 170;
const NEAR_FADE_END = 110;
/** …and out once they've dwindled to this (dp). */
const FAR_FADE_START = 4;
const FAR_FADE_END = 1.2;
/** The blur (Gaussian sigma, dp) over the picture of the app at its strongest: past reading. */
const SNAPSHOT_BLUR = 26;
/** When the blurred picture of the app fades out, as the galaxy comes in beneath it at 0. */
const SNAPSHOT_FADE_START = -0.5;
const SNAPSHOT_FADE_END = 0.35;

export type WelcomeGalaxyLaunchBudget = { glyphs: number; lines: number };

/** As the galaxy's particle budgets: about half as many on a `low` graphics tier phone. */
export const WELCOME_GALAXY_LAUNCH_BUDGETS = {
  android: { glyphs: 120, lines: 70 },
  ios: { glyphs: 150, lines: 90 },
  low: { glyphs: 70, lines: 40 },
  web: { glyphs: 100, lines: 60 },
} as const satisfies Record<string, WelcomeGalaxyLaunchBudget>;

export function welcomeGalaxyLaunchBudget(
  tier: GraphicsTier = getGraphicsTier(),
): WelcomeGalaxyLaunchBudget {
  if (tier === "low") {
    return WELCOME_GALAXY_LAUNCH_BUDGETS.low;
  }
  return Platform.OS === "ios" || Platform.OS === "android" || Platform.OS === "web"
    ? WELCOME_GALAXY_LAUNCH_BUDGETS[Platform.OS]
    : WELCOME_GALAXY_LAUNCH_BUDGETS.web;
}

/** The log of the camera's distance from the splash glyph, relative to where it started. */
export function welcomeGalaxyLaunchLogDistance(t: number): number {
  "worklet";
  const elapsed = Math.max(0, t + WELCOME_GALAXY_LAUNCH_DURATION);
  return 0.5 * LAUNCH_ACCELERATION * elapsed * elapsed;
}

/**
 * How far the sky has turned from the splash's white (0) to the galaxy's own (1): deep space for
 * the dark galaxy, white again for the light one. A launch out of the app starts in the galaxy's
 * own sky.
 */
export function welcomeGalaxyLaunchSkyProgress(
  t: number,
  kind: WelcomeGalaxyLaunchKind = "splash",
): number {
  "worklet";
  return kind === "snapshot" ? 1 : smootherstep(-1.5, -0.1, t);
}

/**
 * How far the characters have turned from black ink into the galaxy's own (0 → 1): its stars, or
 * the light galaxy's coloured inks. It switches over while the sky passes through its middle
 * tones, so on the dark galaxy they keep their contrast against it.
 */
export function welcomeGalaxyLaunchStarness(
  t: number,
  kind: WelcomeGalaxyLaunchKind = "splash",
): number {
  "worklet";
  return smootherstep(0.3, 0.55, welcomeGalaxyLaunchSkyProgress(t, kind));
}

/**
 * How strongly the picture of the app the welcome page launches out of on sign-out is blurred
 * (Gaussian sigma, dp). It is never resized: it starts sharp, exactly the screen the user left,
 * and blurs over as if a frosted pane were fading in across it, until nothing on it can be read.
 */
export function welcomeGalaxyLaunchSnapshotBlur(t: number): number {
  "worklet";
  return SNAPSHOT_BLUR * smootherstep(-WELCOME_GALAXY_LAUNCH_DURATION, -0.9, t);
}

/**
 * The picture of the app on sign-out: whole while it blurs, then, once it is blurred past
 * recognition, fading out as the galaxy comes in beneath it (the flight starts at 0).
 */
export function welcomeGalaxyLaunchSnapshotOpacity(t: number): number {
  "worklet";
  return 1 - smootherstep(SNAPSHOT_FADE_START, SNAPSHOT_FADE_END, t);
}

/**
 * How much of the stand-in's sky (`WelcomeGalaxyStandIn`) covers the still Skia scene beneath it
 * until the scene joins the page's clock. Out of the splash it is whole from the start, since it
 * starts as the splash's own white. Out of a picture of the app (which only the scene can draw,
 * held on its first frame, so it never blurs), it covers the picture as the picture would have
 * faded out.
 */
export function welcomeGalaxyLaunchStandInSkyOpacity(
  t: number,
  kind: WelcomeGalaxyLaunchKind = "splash",
): number {
  "worklet";
  return kind === "snapshot" ? 1 - welcomeGalaxyLaunchSnapshotOpacity(t) : 1;
}

/** The sky's red, green and blue (0–255), through the palette's three. */
function welcomeGalaxyLaunchSkyChannels(
  t: number,
  palette: WelcomeGalaxyPalette,
  kind: WelcomeGalaxyLaunchKind,
): [number, number, number] {
  "worklet";
  const [start, middle, end] = palette.sky;
  const skyProgress = welcomeGalaxyLaunchSkyProgress(t, kind);
  const isUpper = skyProgress < 0.5;
  const from = isUpper ? start : middle;
  const to = isUpper ? middle : end;
  const progress = isUpper ? skyProgress * 2 : skyProgress * 2 - 1;
  return [
    Math.round(lerp(from[0], to[0], progress)),
    Math.round(lerp(from[1], to[1], progress)),
    Math.round(lerp(from[2], to[2], progress)),
  ];
}

/**
 * The sky behind everything: the splash's white, through dusk, to the galaxy's space (from
 * t ≈ 0 on), or for the light galaxy white throughout, but for a breath of pale blue.
 */
export function welcomeGalaxyLaunchBackgroundColor(
  t: number,
  palette: WelcomeGalaxyPalette,
  kind: WelcomeGalaxyLaunchKind = "splash",
): string {
  "worklet";
  const [red, green, blue] = welcomeGalaxyLaunchSkyChannels(t, palette, kind);
  return `rgb(${red}, ${green}, ${blue})`;
}

/** `welcomeGalaxyLaunchBackgroundColor` as an opaque 0xAARRGGBB colour, for the Skia scene. */
export function welcomeGalaxyLaunchBackgroundArgb(
  t: number,
  palette: WelcomeGalaxyPalette,
  kind: WelcomeGalaxyLaunchKind = "splash",
): number {
  "worklet";
  const [red, green, blue] = welcomeGalaxyLaunchSkyChannels(t, palette, kind);
  return (0xff_00_00_00 | (red << 16) | (green << 8) | blue) >>> 0;
}

/** The launch layer fades out once the galaxy flight has taken over. */
export function welcomeGalaxyLaunchLayerOpacity(t: number): number {
  "worklet";
  return 1 - smootherstep(0.15, 0.9, t);
}

/**
 * The launch's characters share the layer's fade. Out of the app they only come in with the
 * galaxy, as the blurred picture fades out over them, leaving the speed lines alone over the blur.
 */
export function welcomeGalaxyLaunchGlyphOpacity(
  t: number,
  kind: WelcomeGalaxyLaunchKind = "splash",
): number {
  "worklet";
  const opacity = welcomeGalaxyLaunchLayerOpacity(t);
  return kind === "snapshot" ? opacity * (1 - welcomeGalaxyLaunchSnapshotOpacity(t)) : opacity;
}

/**
 * Speed lines appear once the camera is properly moving (on sign-out, just after the picture of
 * the app starts to blur), and share the layer's fade.
 */
export function welcomeGalaxyLaunchSpeedLineOpacity(t: number): number {
  "worklet";
  return smootherstep(-1.8, -1.2, t) * welcomeGalaxyLaunchLayerOpacity(t);
}

/**
 * The speed lines' 0xAARRGGBB colour, their fade included: dark streaks against the sky, then
 * pale ones against space (or, on the light galaxy, indigo ink ones against its white).
 */
export function welcomeGalaxyLaunchSpeedLineColor(
  t: number,
  palette: WelcomeGalaxyPalette,
  kind: WelcomeGalaxyLaunchKind = "splash",
): number {
  "worklet";
  const starness = welcomeGalaxyLaunchStarness(t, kind);
  const { speedLineAlpha, speedLineRgb } = palette;
  const alpha = lerp(0.3, speedLineAlpha, starness) * welcomeGalaxyLaunchSpeedLineOpacity(t);
  const red = Math.round(lerp(INK, speedLineRgb[0], starness));
  const green = Math.round(lerp(INK, speedLineRgb[1], starness));
  const blue = Math.round(lerp(INK, speedLineRgb[2], starness));
  return ((Math.round(255 * alpha) << 24) | (red << 16) | (green << 8) | blue) >>> 0;
}

/**
 * The launch's characters, then its speed lines, as struct-of-arrays buffers (like the galaxy's
 * particles). Each is placed by the moment the camera passes it, sampled more densely towards the
 * end, so more and more stream past as the launch speeds up.
 */
export type WelcomeGalaxyLaunchParticles = {
  /** A character's brightness as an alpha (0–255). */
  alpha: Float32Array;
  /** The sprite's offset from its RSXform translation to its cell's centre (see `xformCos`). */
  anchorX: Float32Array;
  anchorY: Float32Array;
  /**
   * A character's colour in the galaxy (0–255 per channel), once it has turned from the splash's
   * ink: a star's, or on the light galaxy a black or graphite ink's.
   */
  blue: Float32Array;
  count: number;
  glyphCount: number;
  green: Float32Array;
  /** What the launch is out of: the splash screen, or a picture of the app on sign-out. */
  kind: WelcomeGalaxyLaunchKind;
  lineCount: number;
  /** `welcomeGalaxyLaunchLogDistance` at the moment the camera passes it. */
  passLogDistance: Float32Array;
  /**
   * How far it sits off the flight axis (dp), and where relative to the vanishing point, when the
   * camera is twice as far as when it passed.
   */
  reach: Float32Array;
  reachX: Float32Array;
  reachY: Float32Array;
  red: Float32Array;
  /** A character's height (dp) when the camera is twice as far as when it passed. */
  size: Float32Array;
  sprite: Uint16Array;
  /**
   * A character's RSXform rotation-and-scale when the camera is twice as far as when it passed:
   * its atlas cell scaled to its height, turned by its tilt.
   */
  xformCos: Float32Array;
  xformSin: Float32Array;
};

export function createWelcomeGalaxyLaunchParticles({
  appearance,
  glyphs,
  glyphSpriteCount,
  kind = "splash",
  lines,
  metrics,
  seed,
}: WelcomeGalaxyLaunchBudget & {
  /** Which galaxy the characters turn into: its stars, or its inks. */
  appearance: WelcomeGalaxyAppearance;
  glyphSpriteCount: number;
  kind?: WelcomeGalaxyLaunchKind;
  metrics: WelcomeGalaxyAtlasMetrics;
  seed: number;
}): WelcomeGalaxyLaunchParticles {
  const random = createWelcomeGalaxyRandom(seed);
  const gaussian = createWelcomeGalaxyGaussian(random);
  const count = glyphs + lines;
  const floats = () => new Float32Array(count);
  const particles: WelcomeGalaxyLaunchParticles = {
    alpha: floats(),
    anchorX: floats(),
    anchorY: floats(),
    blue: floats(),
    count,
    glyphCount: glyphs,
    green: floats(),
    kind,
    lineCount: lines,
    passLogDistance: floats(),
    reach: floats(),
    reachX: floats(),
    reachY: floats(),
    red: floats(),
    size: floats(),
    sprite: new Uint16Array(count),
    xformCos: floats(),
    xformSin: floats(),
  };
  const half = metrics.cellSize / 2;
  const { streamDeep, streamPale } = welcomeGalaxyPalette(appearance).particleColors;
  /** A pass time in [from, to], denser towards `to`. */
  const passLogDistance = (from: number, to: number, bias: number) =>
    welcomeGalaxyLaunchLogDistance(lerp(from, to, random() ** bias));
  const setReach = (index: number, angle: number, reach: number) => {
    particles.reach[index] = reach;
    particles.reachX[index] = reach * Math.cos(angle);
    particles.reachY[index] = reach * Math.sin(angle);
  };

  for (let index = 0; index < glyphs; index += 1) {
    particles.passLogDistance[index] = passLogDistance(-1.75, 0.35, 0.6);
    const angle = random() * 2 * Math.PI;
    setReach(index, angle, lerp(40, 300, random() ** 0.8));
    const size = lerp(14, 34, random());
    particles.size[index] = size;
    const scale = size / metrics.glyphSize;
    // The most common characters, like the galaxy's stream.
    particles.sprite[index] = Math.min(
      glyphSpriteCount - 1,
      Math.floor(glyphSpriteCount * random() ** 1.6),
    );
    const tilt = gaussian() * 0.25;
    const scaledCos = scale * Math.cos(tilt);
    const scaledSin = scale * Math.sin(tilt);
    particles.xformCos[index] = scaledCos;
    particles.xformSin[index] = scaledSin;
    particles.anchorX[index] = half * (scaledCos - scaledSin);
    particles.anchorY[index] = half * (scaledSin + scaledCos);
    particles.alpha[index] = 255 * (0.7 + 0.3 * random());
    // Like the galaxy's stream.
    const [red, green, blue] = welcomeGalaxyMixColor(streamPale, streamDeep, random());
    particles.red[index] = welcomeGalaxyColorByte(red);
    particles.green[index] = welcomeGalaxyColorByte(green);
    particles.blue[index] = welcomeGalaxyColorByte(blue);
  }

  // Out of the app the lines start rushing in as soon as the picture starts to blur, and keep
  // coming over it, rather than building up mostly towards the flight.
  const [linesFrom, linesBias] = kind === "snapshot" ? [-1.85, 0.9] : [-1.7, 0.5];
  for (let index = glyphs; index < count; index += 1) {
    particles.passLogDistance[index] = passLogDistance(linesFrom, 0.5, linesBias);
    const angle = random() * 2 * Math.PI;
    setReach(index, angle, lerp(25, 380, random()));
  }

  return particles;
}

export type WelcomeGalaxyLaunchFrameState = {
  /** The vanishing point: the galaxy core, where the flight picks up. */
  centerX: number;
  centerY: number;
  logDistance: number;
  /** The characters' fade (`welcomeGalaxyLaunchGlyphOpacity`). */
  opacity: number;
  /** `logDistance` a moment ago, for the speed lines' tails. */
  pastLogDistance: number;
  starness: number;
};

export function computeWelcomeGalaxyLaunchFrameState(
  t: number,
  viewport: WelcomeGalaxyViewport,
  kind: WelcomeGalaxyLaunchKind = "splash",
): WelcomeGalaxyLaunchFrameState {
  "worklet";
  const { centerX, centerY } = welcomeGalaxyPlacement(viewport);
  return {
    centerX,
    centerY,
    logDistance: welcomeGalaxyLaunchLogDistance(t),
    opacity: welcomeGalaxyLaunchGlyphOpacity(t, kind),
    pastLogDistance: welcomeGalaxyLaunchLogDistance(t - SPEED_LINE_LAG),
    starness: welcomeGalaxyLaunchStarness(t, kind),
  };
}

/**
 * Writes every launch character's `Atlas` RSXform into `transforms` and its 0xAARRGGBB colour into
 * `colors` (the galaxy sprites' layout). A character still behind the camera is hidden.
 */
export function writeWelcomeGalaxyLaunchGlyphs(
  state: WelcomeGalaxyLaunchFrameState,
  particles: WelcomeGalaxyLaunchParticles,
  transforms: Float32Array,
  colors: Uint32Array,
): void {
  "worklet";
  const { centerX, centerY, logDistance, opacity, starness } = state;
  const count = particles.glyphCount;
  if (opacity <= 0) {
    clearWelcomeGalaxySprites(transforms, colors, 0, count);
    return;
  }
  const { alpha, anchorX, anchorY, blue, green, passLogDistance, reachX, reachY, red, size } =
    particles;
  const { xformCos, xformSin } = particles;
  // Black ink on the sky turning into each character's colour in the galaxy.
  const ink = INK * (1 - starness);
  for (let index = 0; index < count; index += 1) {
    const base = 4 * index;
    // How far the camera has come since passing it, relative to its distance then.
    const passed = Math.exp(logDistance - passLogDistance[index]) - 1;
    const scale = 1 / passed;
    // Its height on screen (dp): it fades in as it shrinks after passing the camera, and out
    // once it has dwindled away.
    const height = scale * size[index];
    if (passed <= 0 || height >= NEAR_FADE_START || height <= FAR_FADE_END) {
      transforms[base] = 0;
      transforms[base + 1] = 0;
      colors[index] = 0;
      continue;
    }
    let fade = opacity * alpha[index];
    if (height > NEAR_FADE_END) {
      const near = (NEAR_FADE_START - height) / (NEAR_FADE_START - NEAR_FADE_END);
      fade *= near * near * near * (near * (near * 6 - 15) + 10);
    } else if (height < FAR_FADE_START) {
      const far = (height - FAR_FADE_END) / (FAR_FADE_START - FAR_FADE_END);
      fade *= far * far * far * (far * (far * 6 - 15) + 10);
    }
    if (fade < 1) {
      transforms[base] = 0;
      transforms[base + 1] = 0;
      colors[index] = 0;
      continue;
    }
    transforms[base] = scale * xformCos[index];
    transforms[base + 1] = scale * xformSin[index];
    transforms[base + 2] = centerX + scale * (reachX[index] - anchorX[index]);
    transforms[base + 3] = centerY + scale * (reachY[index] - anchorY[index]);
    colors[index] =
      (fade << 24) |
      ((ink + starness * red[index]) << 16) |
      ((ink + starness * green[index]) << 8) |
      (ink + starness * blue[index]);
  }
}

/**
 * Writes each speed line that is on screen and long enough to draw as a `[headX, headY, tailX,
 * tailY]` segment into `points`: the head where its point is now, the tail where it was a moment
 * ago. The faster the camera, the longer the line. Returns how many floats it wrote.
 */
export function writeWelcomeGalaxyLaunchSpeedLines(
  state: WelcomeGalaxyLaunchFrameState,
  particles: WelcomeGalaxyLaunchParticles,
  points: Float32Array,
): number {
  "worklet";
  const { centerX, centerY, logDistance, pastLogDistance } = state;
  const { passLogDistance, reach, reachX, reachY } = particles;
  let length = 0;
  for (let index = particles.glyphCount; index < particles.count; index += 1) {
    const passed = Math.exp(logDistance - passLogDistance[index]) - 1;
    if (passed <= 0) {
      continue;
    }
    // Its point's distance from the vanishing point now and a moment ago, as fractions of reach.
    // A tail from before the camera passed the point reaches in from off screen.
    const head = 1 / passed;
    const pastPassed = Math.exp(pastLogDistance - passLogDistance[index]) - 1;
    const tail = Math.min(1 / Math.max(pastPassed, 0.02), 1500 / reach[index]);
    if (head * reach[index] > 1000 || (tail - head) * reach[index] < 1) {
      continue;
    }
    points[length] = centerX + head * reachX[index];
    points[length + 1] = centerY + head * reachY[index];
    points[length + 2] = centerX + tail * reachX[index];
    points[length + 3] = centerY + tail * reachY[index];
    length += 4;
  }
  return length;
}
